import { Button, Group, Select } from '@mantine/core';
import { CLIENT_REQUEST_TYPE_LABELS, CLIENT_REQUEST_TYPES, type ClientRequestSummaryDto, type ClientRequestType, type ClientStage } from '@mexp/shared';
import { stageLabel } from './stage';

export interface RequisitionFilterValues {
  stage: ClientStage | null;
  country: string | null;
  type: ClientRequestType | null;
}

export const NO_FILTERS: RequisitionFilterValues = { stage: null, country: null, type: null };

/** The filters as the URL holds them (`stage`, `country`, `type`); a stage the page does not offer is ignored. */
export function readRequisitionFilters(params: URLSearchParams, stages: readonly ClientStage[]): RequisitionFilterValues {
  const stage = params.get('stage');
  const type = params.get('type');
  return {
    stage: stage && (stages as readonly string[]).includes(stage) ? (stage as ClientStage) : null,
    country: params.get('country'),
    type: type && (CLIENT_REQUEST_TYPES as readonly string[]).includes(type) ? (type as ClientRequestType) : null,
  };
}

export function writeRequisitionFilters(out: URLSearchParams, f: RequisitionFilterValues): void {
  if (f.stage) out.set('stage', f.stage);
  if (f.country) out.set('country', f.country);
  if (f.type) out.set('type', f.type);
}

export function passesFilters(r: ClientRequestSummaryDto, f: RequisitionFilterValues): boolean {
  return (!f.stage || r.stage === f.stage) && (!f.country || r.country === f.country) && (!f.type || r.requestType === f.type);
}

export const filtering = (f: RequisitionFilterValues) => !!(f.stage || f.country || f.type);

/** The company's own countries, A to Z (the chosen one stays listed even when the current view has none). */
export function countriesOf(items: ClientRequestSummaryDto[], chosen: string | null): string[] {
  return [...new Set([...items.map((r) => r.country), ...(chosen ? [chosen] : [])])].sort((a, b) => a.localeCompare(b));
}

/**
 * Stage, country and requisition type, with a way to clear them; used by Dossier requisition and All dossiers, and by
 * the staff Document requests list (which names some stages its own way, through `stageLabels`).
 */
export function RequisitionFilters({
  value,
  onChange,
  stages,
  countries,
  stageLabels,
}: {
  value: RequisitionFilterValues;
  onChange: (next: RequisitionFilterValues) => void;
  stages: readonly ClientStage[];
  countries: string[];
  stageLabels?: Partial<Record<ClientStage, string>>;
}) {
  return (
    <Group gap="sm" wrap="wrap">
      <Select
        aria-label="Stage"
        placeholder="All stages"
        data={stages.map((s) => ({ value: s, label: stageLabels?.[s] ?? stageLabel(s) }))}
        value={value.stage}
        onChange={(v) => onChange({ ...value, stage: v as ClientStage | null })}
        clearable
        w={{ base: '100%', xs: 180 }}
      />
      <Select
        aria-label="Country"
        placeholder="All countries"
        data={countries}
        value={value.country}
        onChange={(v) => onChange({ ...value, country: v })}
        clearable
        searchable
        w={{ base: '100%', xs: 180 }}
      />
      <Select
        aria-label="Requisition type"
        placeholder="All requisition types"
        data={CLIENT_REQUEST_TYPES.map((t) => ({ value: t, label: CLIENT_REQUEST_TYPE_LABELS[t] }))}
        value={value.type}
        onChange={(v) => onChange({ ...value, type: v as ClientRequestType | null })}
        clearable
        w={{ base: '100%', xs: 200 }}
      />
      {filtering(value) && (
        <Button variant="subtle" size="compact-sm" onClick={() => onChange(NO_FILTERS)}>
          Clear filters
        </Button>
      )}
    </Group>
  );
}
